/* eslint-disable */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { devTools } from '@ngneat/elf-devtools';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import './document.patch';

declare global {
  interface Window {
    nativeWindow: {
      digitalData: any;
      _satellite: any;
    };
  }
}

type TrackJsMode = 'development' | 'production';

let trackJsMode: TrackJsMode = 'development' as const;

function onScriptLoad(env: TrackJsMode): void {
  const application = env === 'development' ? 'v2-rscom-dev' : 'v2-rscom-prod';

  (window as any)['TrackJS'].install({
    token: environment.trackjsToken,
    application,
    // for more configuration options, see https://docs.trackjs.com
  });
}

function loadTrackJS(env: TrackJsMode): void {
  if ((window as any)['TrackJS']) {
    console.warn('TrackJS is already loaded.');
    return;
  }

  const script = document.createElement('script');
  script.src = 'https://cdn.trackjs.com/agent/v3/latest/t.js';
  script.type = 'text/javascript';
  script.async = true;

  script.onload = () => onScriptLoad(env);
  document.head.appendChild(script);
}

function loadFixTrackJS() {
  ((doc, adobeAuthEnabledUrl, heroSelector, timeout) => {
    let head = doc.head;
    if (head) {
      if (adobeAuthEnabledUrl) return;
      let style = doc.createElement('style');
      style.id = 'alloyprehiding';
      style.innerText = heroSelector;
      head.appendChild(style);
      setTimeout(() => {
        style.parentNode && style.parentNode.removeChild(style);
      }, timeout);
    }
  })(
    document,
    document.location.href.indexOf('adobe_authoring_enabled') !== -1,
    '.digital-platform-herocarousel { opacity: 0 !important }',
    3000
  );
}

function loadAdobeLaunch(profile: string, key: string): void {
  const adobeLaunchUrl = `//assets.adobedtm.com/ba7cd524c0f9/${profile}/launch-${key}.min.js`;
  const isFound = !!document.head.querySelector(
    `script[src="${adobeLaunchUrl}"]`
  );

  if (!isFound) {
    const adobeScriptElement = document.createElement('script');
    adobeScriptElement.async = true;
    adobeScriptElement.type = 'text/javascript';
    adobeScriptElement.src = adobeLaunchUrl;
    adobeScriptElement.onload = () => {
      document.dispatchEvent(new Event('analyticsLoaded'));
    };
    document.head.appendChild(adobeScriptElement);
  }
}

function loadDynatrace(): void {
  const dynatraceUrl =
    'https://js-cdn.dynatrace.com/jstag/16294a69b33/bf19629get/10e3a58f98e05ef3_complete.js';
  const isFound = !!document.head.querySelector(
    `script[src="${dynatraceUrl}"]`
  );

  if (!isFound) {
    const dtScript = document.createElement('script');
    dtScript.async = true;
    dtScript.type = 'text/javascript';
    dtScript.src = dynatraceUrl;
    dtScript.setAttribute('crossorigin', 'anonymous');
    document.head.appendChild(dtScript);
  }
}

function loadAnalyticsScripts(): void {
  // flag to ensure scripts don't get added to DOM more than once.
  if ((window as any)['didAnalyticsInit']) {
    return;
  }
  (window as any)['didAnalyticsInit'] = true;

  const { adobeConfig, applyAdobeFix } = environment;

  // load scripts
  loadTrackJS(trackJsMode || 'development');
  if (applyAdobeFix) loadFixTrackJS();
  loadDynatrace();

  if (adobeConfig?.length) {
    adobeConfig.forEach((adobe) => {
      loadAdobeLaunch(adobe.profile, adobe.key);
    });
  }
}

function loadAnalyticsScriptsOnEvent(
  event: Event | MouseEvent | TouchEvent
): void {
  loadAnalyticsScripts();
  event.currentTarget?.removeEventListener(
    event.type,
    loadAnalyticsScriptsOnEvent
  ); // remove the event listener that got triggered
}

if (environment.production) {
  enableProdMode();
  trackJsMode = 'production' as const;
} else {
  // Enable Redux dev tools extension for Elf
  devTools();
}

setTimeout(loadAnalyticsScripts, 3500);
document.addEventListener('scroll', loadAnalyticsScriptsOnEvent);
document.addEventListener('mousemove', loadAnalyticsScriptsOnEvent);
document.addEventListener('touchstart', loadAnalyticsScriptsOnEvent);
document.addEventListener('forceAnalyticsLoad', loadAnalyticsScriptsOnEvent);

platformBrowserDynamic()
  .bootstrapModule(AppModule)
  .catch((err) => console.error(err));
